import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    viewChild,
} from '@angular/core';
import {
    AI_CONVO_THINKING_LOADER_VARIANTS,
    AIConvoThinkingLoaderPose,
    AIConvoThinkingLoaderSpring,
    getThinkingLoaderPose,
    stepThinkingLoaderSpring,
} from '@modules/ai-convo/helpers/ai-convo-thinking-loader';

// The logo's own colours, as in ai-interacta-logo_light.svg and _dark.svg.
const COLORS = {
    light: { big: '#FF395A', small: '#1B212B' },
    dark: { big: '#ED5E76', small: '#FFFFFF' },
} as const;

let nextVariant = 0;

@Component({
    selector: 'interacta-ai-convo-thinking-loader',
    templateUrl: './ai-convo-thinking-loader.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'relative isolate block h-21 w-44 shrink-0' },
})
export class AIConvoThinkingLoaderComponent {
    readonly isDark = input<boolean>(false);
    /** Thoughts shown so far: the variant that reacts to them turns once for each. */
    readonly thoughts = input<number>(0);

    protected readonly colors = computed(() =>
        this.isDark() ? COLORS.dark : COLORS.light,
    );

    protected readonly starMask =
        'url(./assets/shared/images/ai/ai-interacta-star.svg)';

    private readonly config =
        AI_CONVO_THINKING_LOADER_VARIANTS[
            nextVariant++ % AI_CONVO_THINKING_LOADER_VARIANTS.length
        ];

    private spring: AIConvoThinkingLoaderSpring = { angle: 0, velocity: 0 };

    private readonly big = viewChild.required<ElementRef<HTMLElement>>('big');
    private readonly small =
        viewChild.required<ElementRef<HTMLElement>>('small');

    constructor() {
        const destroyRef = inject(DestroyRef);

        afterNextRender(() => {
            const query =
                typeof matchMedia === 'function'
                    ? matchMedia('(prefers-reduced-motion: reduce)')
                    : null;
            let frame = 0;

            const stop = (): void => {
                cancelAnimationFrame(frame);
                frame = 0;
            };

            const run = (): void => {
                const start = performance.now();
                let last = start;

                const tick = (now: number): void => {
                    const dt = Math.min(0.05, (now - last) / 1000);
                    last = now;
                    this.spring = stepThinkingLoaderSpring(
                        this.spring,
                        this.thoughts() * 360,
                        dt,
                    );

                    this.apply(
                        getThinkingLoaderPose(
                            now - start,
                            this.config,
                            this.spring.angle,
                        ),
                    );
                    frame = requestAnimationFrame(tick);
                };

                frame = requestAnimationFrame(tick);
            };

            const sync = (): void => {
                stop();
                if (query?.matches) {
                    this.apply(getThinkingLoaderPose(0, this.config));
                } else {
                    run();
                }
            };

            sync();
            query?.addEventListener?.('change', sync);
            destroyRef.onDestroy(() => {
                stop();
                query?.removeEventListener?.('change', sync);
            });
        });
    }

    private apply(pose: AIConvoThinkingLoaderPose): void {
        this.big().nativeElement.style.transform = `rotate(${pose.bigRotation}deg) scale(${pose.bigScale})`;

        const small = this.small().nativeElement;
        small.style.transform = `translate(${pose.smallX}px, ${pose.smallY}px) scale(${pose.smallScale})`;
        small.style.zIndex = pose.smallInFront ? '20' : '0';
    }
}
